/**
 * Rooms (design 2F): deliberately small — name (required), code, type
 * (free text), gap, description, enable/disable, supported services, and
 * (ZOY-636) the room's hours and blockouts, which ride the availability
 * permission rather than rooms.manage. No delete anywhere. List runs on the
 * numbered query contract (ZOY-285).
 */

import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import a from '../access/access.module.css';
import { Button, Chip, Notice, TextField } from '@/ui/kit';
import { Column, Pager, PrimaryCell, RowButton, TableSearch, WorkTable } from '@/ui/WorkTable';
import { FieldGrid, FormCard, Span2, TextAreaField, Toggle } from '@/ui/formkit';
import { useNumberedList } from '@/core/api/usePagedList';
import type { QueryFilter } from '@/core/api/querySpec';
import {
  addRoomCapability,
  createRoom,
  listRoomCapabilities,
  listRooms,
  listServices,
  modesOf,
  patchRoom,
  removeRoomCapability,
  setRoomDisabled,
  type RoomView,
} from '@/core/clinic/setupApi';
import { useAuthz } from '@/core/authz/AuthzProvider';
import { PERM } from '@/core/authz/permissions';
import { useDebounced } from '@/core/api/useDebounced';
import { BUFFER_RANGE, integerField, integerProblemText } from './numericField';
import { SaveErrorNotice, saveErrorOf, type SaveError } from './saveError';
import { RoomAvailabilityCard } from './RoomAvailability';

type View = { kind: 'list' } | { kind: 'edit'; room: RoomView | null };

export function RoomsTab() {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const authz = useAuthz();
  const manage = authz.can(PERM.roomsManage);
  const canCapsRead = authz.can(PERM.capabilitiesRead);
  const canCapsManage = authz.can(PERM.capabilitiesManage);
  // ZOY-636: room hours are availability, not room management.
  const canHoursRead = authz.can(PERM.availabilityRead);
  const canHoursManage = authz.can(PERM.availabilityManage);
  const queryClient = useQueryClient();
  const [view, setView] = useState<View>({ kind: 'list' });
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search, 300);

  const filters = useMemo<QueryFilter[]>(
    () =>
      debounced.trim() === '' ? [] : [{ field: 'name', op: 'contains', value: debounced.trim() }],
    [debounced],
  );

  const list = useNumberedList<RoomView>({
    keyBase: ['rooms', authz.clinicId],
    fetchPage: (spec) => listRooms(spec),
    filters,
    sort: [{ field: 'name', dir: 'asc' }],
  });

  const toggle = useMutation({
    mutationFn: (r: RoomView) => setRoomDisabled(r.id, !r.disabled),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['rooms'] }),
  });

  if (view.kind === 'edit') {
    // ZOY-405: without rooms.manage the detail is facts only — edit inputs,
    // the bookable toggle and capability changes are ABSENT, not disabled.
    if (!manage) {
      return (
        <RoomDetails
          room={view.room!}
          showCapabilities={canCapsRead}
          hours={canHoursRead ? { manage: canHoursManage } : null}
          onBack={() => setView({ kind: 'list' })}
        />
      );
    }
    return (
      <RoomEditor
        room={view.room}
        capsRead={canCapsRead}
        capsManage={canCapsManage}
        hours={canHoursRead ? { manage: canHoursManage } : null}
        onDone={() => {
          void queryClient.invalidateQueries({ queryKey: ['rooms'] });
          setView({ kind: 'list' });
        }}
        onCancel={() => setView({ kind: 'list' })}
      />
    );
  }

  const columns: Column[] = [
    { id: 'room', label: t('rooms.colRoom') as string, width: '1.7fr' },
    { id: 'type', label: t('rooms.colType') as string, width: '1fr', tier: 'tablet-up' },
    { id: 'status', label: t('rooms.colStatus') as string, width: 'auto', tier: 'tablet-up' },
    { id: 'actions', label: t('staff.colActions') as string, width: 'auto', align: 'end' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <p className={a.pageSub} style={{ margin: 0 }}>
          {t('rooms.sub')}
        </p>
        {manage && (
          <Button variant="primary" onClick={() => setView({ kind: 'edit', room: null })}>
            {t('rooms.add')}
          </Button>
        )}
      </div>

      <WorkTable<RoomView>
        columns={columns}
        rows={list.view.rows}
        rowKey={(r) => r.id}
        loading={list.loading}
        error={list.error !== null}
        onRetry={list.refetch}
        toolbar={
          <TableSearch
            value={search}
            onChange={setSearch}
            placeholder={t('rooms.colRoom') as string}
          />
        }
        empty={{
          title: t(debounced ? 'table.noResultsTitle' : 'rooms.emptyTitle') as string,
          body: t(debounced ? 'table.noResultsBody' : 'rooms.emptyBody') as string,
        }}
        renderCell={(r, col) => {
          switch (col) {
            case 'room':
              return (
                <PrimaryCell
                  avatar={r.code ?? '•'}
                  main={r.name}
                  sub={r.code ?? (t('rooms.noCode') as string)}
                />
              );
            case 'type':
              return (
                r.roomType ?? <span style={{ color: 'var(--zc-faint)' }}>{t('rooms.noType')}</span>
              );
            case 'status':
              return r.disabled ? (
                <Chip kind="quiet">{t('common.disabled')}</Chip>
              ) : (
                <Chip kind="ok">{t('common.enabled')}</Chip>
              );
            case 'actions':
              return (
                <span style={{ display: 'flex', gap: 8 }}>
                  <RowButton onClick={() => setView({ kind: 'edit', room: r })}>
                    {t('common.view')}
                  </RowButton>
                  {manage && (
                    <RowButton danger={!r.disabled} onClick={() => toggle.mutate(r)}>
                      {r.disabled ? t('common.enable') : t('common.disable')}
                    </RowButton>
                  )}
                </span>
              );
            default:
              return null;
          }
        }}
        footer={
          <Pager
            view={list.view}
            perPage={list.perPage}
            onPerPage={list.setPerPage}
            onPage={list.goTo}
          />
        }
      />
      <Notice kind="info">{t('rooms.noDelete')}</Notice>
    </div>
  );
}

/** Facts-only room view for rooms.read without rooms.manage (ZOY-405). */
export function RoomDetails(props: {
  room: RoomView;
  showCapabilities: boolean;
  /** ZOY-636: null without availability.read; `manage` with availability.manage. */
  hours: { manage: boolean } | null;
  onBack: () => void;
}) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const r = props.room;
  const rows: [string, React.ReactNode][] = [
    [t('rooms.name'), r.name],
    [t('rooms.code'), r.code ?? '—'],
    [t('rooms.type'), r.roomType ?? '—'],
    [t('rooms.buffer'), r.bufferMinutes !== null ? intl.formatNumber(r.bufferMinutes) : '—'],
    [t('rooms.description'), r.description ?? '—'],
    [
      t('rooms.colStatus'),
      r.disabled ? (
        <Chip kind="quiet">{t('common.disabled')}</Chip>
      ) : (
        <Chip kind="ok">{t('common.enabled')}</Chip>
      ),
    ],
  ];
  return (
    <div style={{ maxInlineSize: 680, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className={a.sectionCard} style={{ inlineSize: '100%' }}>
        <div style={{ padding: '16px 18px 6px' }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--zc-teal-deep)' }}>{r.name}</h2>
        </div>
        {rows.map(([k, v]) => (
          <div key={String(k)} className={a.row}>
            <span className={a.rowKey}>{k}</span>
            <span className={a.rowValue}>{v}</span>
          </div>
        ))}
      </div>
      {props.showCapabilities && <RoomServicesCard roomId={r.id} canManage={false} />}
      {props.hours && <RoomAvailabilityCard roomId={r.id} manage={props.hours.manage} />}
      <div>
        <Button variant="quiet" onClick={props.onBack}>
          {t('common.back')}
        </Button>
      </div>
    </div>
  );
}

function RoomEditor(props: {
  room: RoomView | null;
  capsRead: boolean;
  capsManage: boolean;
  hours: { manage: boolean } | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const editing = props.room !== null;
  const [name, setName] = useState(props.room?.name ?? '');
  const [code, setCode] = useState(props.room?.code ?? '');
  const [roomType, setRoomType] = useState(props.room?.roomType ?? '');
  const [buffer, setBuffer] = useState(
    props.room?.bufferMinutes !== null && props.room?.bufferMinutes !== undefined
      ? String(props.room.bufferMinutes)
      : '',
  );
  const [description, setDescription] = useState(props.room?.description ?? '');
  const [enabled, setEnabled] = useState(!(props.room?.disabled ?? false));
  const [error, setError] = useState<SaveError | null>(null);

  // ZOY-637: the gap is validated in words before anything is sent. Clearing a
  // stored gap sends null (the backend unsets it); an empty field that was
  // already empty is left out, so the audit never records a change that
  // did not happen.
  const storedBuffer = props.room?.bufferMinutes ?? null;
  const bufferField = integerField(buffer, BUFFER_RANGE);
  const bufferError =
    bufferField.problem !== null
      ? integerProblemText(intl, bufferField.problem, BUFFER_RANGE)
      : undefined;

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name: name.trim(),
        code: code.trim() || undefined,
        roomType: roomType.trim() || undefined,
        description: description.trim() || undefined,
        bufferMinutes: bufferField.value ?? (storedBuffer !== null ? null : undefined),
      };
      const saved = editing ? await patchRoom(props.room!.id, body) : await createRoom(body);
      if (saved.disabled === enabled) await setRoomDisabled(saved.id, !enabled);
      return saved;
    },
    onMutate: () => setError(null),
    onSuccess: props.onDone,
    onError: (err) => setError(saveErrorOf(err)),
  });

  const opt = { word: t('signin.optional'), kind: 'optional' as const };

  return (
    <div style={{ maxInlineSize: 680, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <FormCard
        title={editing ? props.room!.name : (t('rooms.add') as string)}
        onSubmit={() => save.mutate()}
        cta={editing ? (t('common.save') as string) : (t('rooms.createCta') as string)}
        ctaDisabled={name.trim() === '' || bufferError !== undefined}
        ctaDisabledReason={bufferError !== undefined ? (t('field.fixFirst') as string) : undefined}
        busy={save.isPending}
        secondary={{ label: t('common.cancel'), onClick: props.onCancel }}
      >
        {error && <SaveErrorNotice error={error} />}
        <FieldGrid>
          <TextField
            label={t('rooms.name')}
            value={name}
            onChange={setName}
            requirement={{ word: t('signin.required'), kind: 'required' }}
          />
          <TextField
            label={t('rooms.code')}
            value={code}
            onChange={setCode}
            help={t('rooms.codeHelp')}
            requirement={opt}
            machine
          />
          <TextField
            label={t('rooms.type')}
            value={roomType}
            onChange={setRoomType}
            help={t('rooms.typeHelp')}
            requirement={opt}
          />
          <TextField
            label={t('rooms.buffer')}
            value={buffer}
            onChange={setBuffer}
            help={t('rooms.bufferHelp')}
            error={bufferError}
            requirement={opt}
            inputMode="numeric"
            machine
          />
          <Span2>
            <TextAreaField
              label={t('rooms.description')}
              value={description}
              onChange={setDescription}
              requirement={opt}
              rows={2}
            />
          </Span2>
          <Span2>
            <Toggle
              label={t('rooms.available')}
              help={t('rooms.availableHelp')}
              checked={enabled}
              onChange={setEnabled}
              stateWord={
                enabled ? (t('common.enabled') as string) : (t('common.disabled') as string)
              }
            />
          </Span2>
        </FieldGrid>
      </FormCard>
      {editing && props.capsRead && (
        <RoomServicesCard roomId={props.room!.id} canManage={props.capsManage} />
      )}
      {editing && props.hours && (
        <RoomAvailabilityCard roomId={props.room!.id} manage={props.hours.manage} />
      )}
    </div>
  );
}

/**
 * Supported services — room↔service capability, in-clinic services only.
 * With capabilities.manage the chips toggle grants; without it (ZOY-405)
 * the card lists the granted services as plain facts — capability rows
 * carry their service names, so no catalogue read is needed.
 */
function RoomServicesCard({ roomId, canManage }: { roomId: string; canManage: boolean }) {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const queryClient = useQueryClient();

  const capsQuery = useQuery({
    queryKey: ['room-caps'],
    queryFn: () => listRoomCapabilities(),
  });
  const servicesQuery = useQuery({
    queryKey: ['services-for-rooms'],
    queryFn: () => listServices({ limit: 100, sort: [{ field: 'name', dir: 'asc' }] }),
    staleTime: 5 * 60_000,
    enabled: canManage,
  });

  const caps = (capsQuery.data?.items ?? []).filter((c) => c.roomId === roomId);
  const capByService = new Map(caps.map((c) => [c.serviceId, c]));
  // Only in-clinic-capable services matter for a room (ZOY-693: online
  // sessions never take a room either).
  const services = (servicesQuery.data?.items ?? []).filter((s) =>
    modesOf(s).includes('in_clinic'),
  );

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['room-caps'] });
  const grant = useMutation({
    mutationFn: (serviceId: string) => addRoomCapability({ roomId, serviceId }),
    onSuccess: invalidate,
  });
  const revoke = useMutation({ mutationFn: removeRoomCapability, onSuccess: invalidate });

  return (
    <div className={a.sectionCard} style={{ inlineSize: '100%' }}>
      <div style={{ padding: '14px 18px 4px' }}>
        <strong style={{ color: 'var(--zc-teal-deep)' }}>{t('rooms.supported')}</strong>
        <p style={{ fontSize: 12.5, color: 'var(--zc-muted)', marginBlockStart: 4 }}>
          {t('rooms.supportedHelp')}
        </p>
      </div>
      {!canManage ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '10px 18px 16px' }}>
          {caps.map((c) => (
            <Chip key={c.id} kind="ok">
              {c.serviceName}
            </Chip>
          ))}
          {caps.length === 0 && <span style={{ color: 'var(--zc-faint)', fontSize: 13 }}>—</span>}
        </div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '10px 18px 16px' }}>
          {services.map((svc) => {
            const cap = capByService.get(svc.id);
            const on = cap !== undefined;
            return (
              <button
                key={svc.id}
                type="button"
                onClick={() => (on ? revoke.mutate(cap.id) : grant.mutate(svc.id))}
                style={{
                  minBlockSize: 40,
                  paddingInline: 14,
                  borderRadius: 999,
                  border: `1.5px solid ${on ? 'var(--zc-sage-dot)' : 'var(--zc-line-strong)'}`,
                  background: on ? 'var(--zc-sage-bg)' : 'var(--zc-card)',
                  color: on ? 'var(--zc-sage-ink)' : 'var(--zc-ink-2)',
                  fontSize: 13,
                  fontWeight: on ? 700 : 500,
                  cursor: 'pointer',
                }}
                aria-pressed={on}
              >
                {svc.name}
              </button>
            );
          })}
          {services.length === 0 && (
            <span style={{ color: 'var(--zc-faint)', fontSize: 13 }}>—</span>
          )}
        </div>
      )}
    </div>
  );
}
